import React from 'react';
import {
  styled,
  Box,
  Typography,
  Collapse,
  Switch,
  Tooltip,
} from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { FormData, PermissionGroupItem } from '../types';
import Spacing from 'components/Spacing';
import { usePermissionGroup } from './usePermissionGroup';

type PermissionGroupBoxProps = {
  title: string;
  description: string;
  items: Array<PermissionGroupItem>;
  allItems: Array<PermissionGroupItem>;
  defaultExpanded?: boolean;
  form: UseFormReturn<FormData>;
  userPermissions?: Array<string>;
  readOnly?: boolean;
};

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.borderColor.dark}`,
  overflow: 'hidden',
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.md),
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
}));

const HeaderDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.fontSizes.regular,
  color: '#666666',
  lineHeight: 1.5,
}));

const CaretIcon = styled(Box)({
  color: '#4caf50',
  display: 'flex',
  alignItems: 'center',
  marginLeft: '16px',
});

const Divider = styled(Box)(({ theme }) => ({
  height: '1px',
  backgroundColor: theme.palette.borderColor.main,
}));

const PermissionItemsContainer = styled(Box)(({ theme }) => ({
  padding: `0 ${theme.spacing(theme.spacingSizes.sm)}`,
}));

const PermissionItemRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled?: boolean }>(({ theme, disabled }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.7 : 1,

  '&:hover': {
    backgroundColor: disabled ? undefined : '#F8F8F8',
  },
}));

const PermissionLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.fontSizes.regular,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.text.darkBodyText,
}));

const PermissionDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.fontSizes.sm,
  color: '#928C8C',
  lineHeight: 1.5,
}));

const PermissionGroupBox: React.FC<PermissionGroupBoxProps> = ({
  title,
  description,
  items,
  allItems,
  defaultExpanded = true,
  form,
  userPermissions,
  readOnly,
}) => {
  const {
    expanded,
    setExpanded,
    isDisabled,
    isRestrictedByUserPermissions,
    isItemChecked,
    getRequiredItem,
    isRequirementMet,
    onChange,
  } = usePermissionGroup({
    items,
    allItems,
    defaultExpanded,
    form,
    userPermissions,
    readOnly,
  });

  return (
    <Card>
      <Header
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <Spacing size="xs">
          <Typography variant="h4">{title}</Typography>
          <HeaderDescription>{description}</HeaderDescription>
        </Spacing>
        <CaretIcon>
          {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </CaretIcon>
      </Header>
      <Collapse in={expanded}>
        <PermissionItemsContainer>
          <Divider />
          {items.map((item, index) => {
            const restricted = isRestrictedByUserPermissions(item);
            const disabled = isDisabled(item) || restricted;

            const permissionRow = (
              <PermissionItemRow
                onClick={onChange(item)}
                disabled={readOnly ?? disabled}
              >
                <Spacing size="xs">
                  <PermissionLabel>{item.label}</PermissionLabel>
                  {item.requiresId && !isRequirementMet(item) ? (
                    <PermissionDescription>
                      Requires "{getRequiredItem(item)?.label}" to be enabled
                    </PermissionDescription>
                  ) : item.description ? (
                    <PermissionDescription>
                      {item.description}
                    </PermissionDescription>
                  ) : null}
                </Spacing>

                <Switch
                  checked={isItemChecked(item)}
                  onChange={onChange(item)}
                  disabled={disabled}
                />
              </PermissionItemRow>
            );

            return (
              <React.Fragment key={item.label}>
                {restricted ? (
                  <Tooltip
                    title="You cannot assign permissions you don't have yourself."
                    arrow
                  >
                    {permissionRow}
                  </Tooltip>
                ) : (
                  permissionRow
                )}
                {index < items.length - 1 ? <Divider /> : null}
              </React.Fragment>
            );
          })}
        </PermissionItemsContainer>
      </Collapse>
    </Card>
  );
};

export default PermissionGroupBox;
